import { defineStore } from "pinia";

import { FileManagerState, Filters } from "../interfaces/file-manager";
import { Filter, Facet, FacetCount } from "../interfaces/facets";
import { QueryParams } from "../interfaces/query-parameters";

import { useSearchParameters, useTypesenseClient } from "../composables/typesense";
import { useQueryArrayUrlify, useRemoveQueryUrlify } from "../composables/query-parameters";

export const useFileManagerStore = defineStore("file-manager", {
	state: (): FileManagerState => ({
		loading: false,
		facets: [],
		facetsAfterFilter: [],
		filters: {
			searchQuery: "",
			filterQuery: "",
			year: {
				field: "year",
				values: [],
				combination: "&&",
				disabled: false,
			},
			month: {
				field: "month",
				values: [],
				combination: "&&",
				disabled: false,
			},
			meeting: {
				field: "meetingType",
				values: [],
				combination: "&&",
				disabled: false,
			},
			report: {
				field: "reportType",
				values: [],
				combination: "&&",
				disabled: false,
			},
			corporate: {
				field: "corporateType",
				values: [],
				combination: "&&",
				disabled: false,
			},
		},
		pagination: {
			current: 0,
			totalPages: null,
			totalResults: null,
			foundResults: null,
			itemsPerPage: 24,
		},
		sorting: {
			type: "post_date_timestamp",
			order: "desc",
		},
		index: import.meta.env.VITE_FILE_MANAGER_INDEX ?? "wdp_production_file_manager_docs",
		documents: null,
		owner: null,
	}),
	actions: {
		async fetch(owner: string, reset = false, queryParams?: Array<QueryParams>) {
			this.owner = owner;
			this.loading = true;

			// set URL query parameters if existing
			if (queryParams) {
				this.setQueryFilters(queryParams);
			} else {
				this.setFiltersQuery(owner);
			}

			// check if a filter has been set
			if (
				this.filters.year.values.length === 0 &&
				this.filters.month.values.length === 0 &&
				this.filters.meeting.values.length === 0 &&
				this.filters.report.values.length === 0 &&
				this.filters.corporate.values.length === 0 &&
				this.filters.searchQuery === ""
			) {
				this.pagination.itemsPerPage = 24;
			} else {
				this.pagination.itemsPerPage = 250;
			}

			// connect to typesense
			const client = useTypesenseClient();
			const searchParameters = useSearchParameters(this.filters, this.sorting);

			if (!reset && this.pagination.current) {
				this.pagination.current += 1;
			} else {
				this.pagination.current = 1;
				this.pagination.totalPages = 0;
				this.pagination.totalResults = null;
			}

			// setup multi search for filtering
			const searchRequests = {
				searches: [
					{
						highlight_full_fields: "title",
						collection: this.index,
						facet_by: "year,meetingType,corporateType,reportType,month",
						max_facet_values: 100,
						page: this.pagination.current,
						per_page: this.pagination.itemsPerPage,
						...searchParameters,
					},
					{
						query_by: "title",
						sort_by: "post_date_timestamp:desc",
						collection: this.index,
						q: "*",
						facet_by: "year,meetingType,corporateType,reportType,month",
						max_facet_values: 100,
						page: 1,
						per_page: 1,
						filter_by: `owner:${owner}&&site:${window.language}`,
					},
				],
			};

			await client.multiSearch.perform(searchRequests).then((results: any) => {
				const filteredResults = results.results?.[0];

				if (!filteredResults) {
					console.error("Typesense multi-search returned invalid results.");
					this.loading = false;
					return;
				}

				this.facets = results.results?.[1]?.facet_counts || [];
				this.facetsAfterFilter = filteredResults.facet_counts || [];

				this.pagination.current = filteredResults.page;
				this.pagination.totalPages = this.pagination.itemsPerPage
					? Math.ceil(filteredResults.found / this.pagination.itemsPerPage)
					: 0;
				this.pagination.foundResults = filteredResults.found;
				this.pagination.totalResults = filteredResults.out_of;

				this.facets.forEach((facet: Facet) => {
					if (facet.counts) {
						facet.counts = facet.counts.filter((count: FacetCount) => count.value !== "all");
					}
				});
				this.facets = this.facets.filter((facet: Facet) => facet.counts && facet.counts.length > 0);

				this.facetsAfterFilter.forEach((facet: Facet) => {
					if (facet.counts) {
						facet.counts = facet.counts.filter((count: FacetCount) => count.value !== "all");
					}
				});
				this.facetsAfterFilter = this.facetsAfterFilter.filter(
					(facet: Facet) => facet.counts && facet.counts.length > 0,
				);

				if (filteredResults.hits) {
					const hits: Array<any> = filteredResults.hits;

					if (reset) {
						this.documents = hits;
					} else {
						this.documents = [...(this.documents || []), ...hits];
					}
				}

				setTimeout(() => {
					this.loading = false;
				}, 500);
			});
		},
		resetFilters(fetch = true) {
			this.filters.searchQuery = "";
			this.filters.filterQuery = "";
			this.filters.year.values = [];
			this.filters.month.values = [];
			this.filters.meeting.values = [];
			this.filters.report.values = [];
			this.filters.corporate.values = [];

			useRemoveQueryUrlify("year");
			useRemoveQueryUrlify("month");
			useRemoveQueryUrlify("meetingType");
			useRemoveQueryUrlify("reportType");
			useRemoveQueryUrlify("corporateType");

			if (this.owner) {
				this.setFiltersQuery(this.owner);

				if (fetch) {
					this.fetch(this.owner, true);
				}
			}
		},
		setFilter(type: keyof Filters, value: string, disabled = false, fetch = true) {
			const settedFilter: Filter | string = this.filters[type];

			if (typeof settedFilter !== "string") {
				if (settedFilter.values.indexOf(value) < 0) {
					settedFilter.values.push(value);
					useQueryArrayUrlify(type, value);
				} else {
					settedFilter.values = settedFilter.values.filter((item: string) => item !== value);
					useRemoveQueryUrlify(type, value);
				}
				settedFilter.disabled = disabled;
			}

			this.setFiltersQuery(this.owner);

			if (fetch) {
				this.fetch(this.owner, true);
			}
		},

		setFiltersQuery(owner: string) {
			const filterParts: string[] = [];
			const language = window.language;

			filterParts.push(`owner:${owner}`);
			filterParts.push(`site:${language}`);

			if (this.filters.year.values.length > 0) {
				filterParts.push(`${this.filters.year.field}:=[${this.filters.year.values.join(",")}]`);
			}

			if (this.filters.month.values.length > 0) {
				filterParts.push(`${this.filters.month.field}:=[${this.filters.month.values.join(",")}]`);
			}

			if (this.filters.meeting.values.length > 0) {
				filterParts.push(`${this.filters.meeting.field}:=[${this.filters.meeting.values.join(",")}]`);
			}

			if (this.filters.report.values.length > 0) {
				filterParts.push(`${this.filters.report.field}:=[${this.filters.report.values.join(",")}]`);
			}

			if (this.filters.corporate.values.length > 0) {
				const corporateFilters = this.filters.corporate.values.map(
					(value) => `${this.filters.corporate.field}:=${value}`,
				);
				filterParts.push(corporateFilters.join(` ${this.filters.corporate.combination} `));
			}

			this.filters.filterQuery = filterParts.join(" && ");
		},

		setSearch(value: string) {
			this.filters.searchQuery = value;
			this.fetch(this.owner, true);
		},
		setQueryFilters(queryParams: Array<QueryParams>) {
			for (const key in queryParams as QueryParams[]) {
				const values = queryParams[key].values;
				const type: string = queryParams[key].type;

				if (values && this.filters[type as keyof Filters]) {
					(this.filters[type as keyof Filters] as Filter).values = values.map(
						(item: { name: string }) => item.name,
					);
				}
			}
			this.setFiltersQuery(this.owner);
		},
	},
});
